// ECO7 KITCHEN SUPPRESSION PDF REPORT GENERATOR v1.2
// Color: #21592d for headers, title updated, per-kitchen formatted, footer normal
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { LOGO_BASE64, LOGO_FORMAT } from './pdfReportGenerator';

const ECO7_GREEN = [33, 89, 45];  // #21592d
const BRAND_NAVY = [26, 39, 68];
const GRAY       = [100, 100, 100];
const WHITE      = [255, 255, 255];
const VER        = "1.0.0";

function fmtDate() { return new Date().toISOString().replace("T", " ").slice(0, 19); }

function addFooter(doc, pg) {
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.text("Software Version " + VER + "    Calculation Performed: " + fmtDate() + " EST", pw / 2, ph - 15, { align: "center" });
  doc.text("Page " + pg, pw - 20, ph - 15, { align: "right" });
  doc.text("Copyright \u00A9 Boal Systems Inc. Fort Lauderdale, FL USA", pw / 2, ph - 10, { align: "center" });
}

function addLogo(doc, x, y, w, h) {
  try {
    if (LOGO_BASE64) {
      doc.setFillColor(255, 255, 255);
      doc.rect(x, y, w, h, "F");
      doc.addImage(LOGO_BASE64, LOGO_FORMAT || "JPEG", x, y, w, h);
    }
  } catch (e) {}
}

function lv(doc, label, value, x, y, lw) {
  doc.setFont("helvetica", "normal"); doc.setTextColor(...GRAY);
  doc.text(label, x, y);
  doc.setTextColor(0, 0, 0);
  doc.text(String(value || "\u2014").substring(0, 60), x + (lw || 48), y);
}

function pageHeader(doc, title) {
  const pw = doc.internal.pageSize.getWidth(), m = 20;
  addLogo(doc, m, 8, 25, 25);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8); doc.setTextColor(...GRAY);
  doc.text("Eco7 Kitchen Suppression System Report", m + 28, 18);
  doc.setFontSize(14); doc.setFont("helvetica", "bold"); doc.setTextColor(...BRAND_NAVY);
  doc.text(title, pw / 2, 42, { align: "center" });
}

// ─── Public entry points ──────────────────────────────────────
export function generateEco7Report(form, result, isMetric) {
  const doc = buildEco7Doc(form, result, isMetric);
  const fn  = (form.projectName || "Eco7_Kitchen").replace(/[^a-zA-Z0-9_-]/g, "_") + "_BOM.pdf";
  doc.save(fn);
}

export function generateEco7ReportBase64(form, result, isMetric) {
  const doc = buildEco7Doc(form, result, isMetric);
  const fn  = (form.projectName || "Eco7_Kitchen").replace(/[^a-zA-Z0-9_-]/g, "_") + "_BOM.pdf";
  return { base64: doc.output("datauristring").split(",")[1], fileName: fn, mimeType: "application/pdf" };
}

// ─── Main builder ─────────────────────────────────────────────
function buildEco7Doc(form, result, isMetric) {
  const kitchenResults = result.kitchenResults || [];
  const aggBom         = result.aggregatedBom  || [];
  const uL             = isMetric ? "m" : "ft";
  const doc            = new jsPDF("p", "mm", "letter");
  const pw             = doc.internal.pageSize.getWidth();
  const m              = 20;
  let pg               = 1;

  // ═══ PAGE 1 ═══
  addLogo(doc, pw / 2 - 20, 10, 40, 40);
  doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.setTextColor(...ECO7_GREEN);
  doc.text("Eco7 Kitchen Suppression System Report", pw / 2, 60, { align: "center" });
  doc.setFontSize(14); doc.setTextColor(0, 0, 0);
  doc.text(form.company || form.designerName || "\u2014", pw / 2, 72, { align: "center" });

  let y = 84;
  doc.setFontSize(10);
  [["Designer:", form.designerName], ["Email:", form.email], ["Phone:", form.phone]].forEach(p => {
    doc.setFont("helvetica", "bold"); doc.setTextColor(...ECO7_GREEN); doc.text(p[0], m, y);
    doc.setFont("helvetica", "normal"); doc.setTextColor(0, 0, 0); doc.text(String(p[1] || "\u2014"), m + 35, y); y += 6;
  });

  // Project Info
  y += 4;
  doc.setFontSize(12); doc.setFont("helvetica", "bold"); doc.setTextColor(...ECO7_GREEN);
  doc.text("Project Information", pw / 2, y, { align: "center" }); y += 8; doc.setFontSize(9);
  [["Project Name:", form.projectName], ["Location:", form.projectLocation], ["Country:", form.country], ["Notes:", form.notes]].forEach(p => {
    lv(doc, p[0], p[1], m, y, 35); y += 5.5;
  });

  // System Configuration
  y += 4;
  doc.setFontSize(12); doc.setFont("helvetica", "bold"); doc.setTextColor(...ECO7_GREEN);
  doc.text("System Configuration", pw / 2, y, { align: "center" }); y += 8; doc.setFontSize(9);

  const lc = m, rc = pw / 2 + 5;
  const leftCols = [
    ["Unit System:",         isMetric ? "Metric" : "Imperial"],
    ["Detection Type:",      form.detectionType || "\u2014"],
    ["Pressure Supervisor:", form.pressureSupervisor || "\u2014"],
  ];
  const rightCols = [
    ["Gas Shut-off Valve:",  form.gasValve || "\u2014"],
    ["Valve Diameter:",      form.gasValve === "Yes / S\u00ED" ? (form.gasValveDiameter || "\u2014") : "N/A"],
    ["Manual Release:",      form.manualRelease || "\u2014"],
  ];
  if (form.detectionType === "Electronic Heat Detector") {
    leftCols.push(["Supervisable Solenoid:", form.supervisableSolenoid || "\u2014"]);
  }
  const maxRows = Math.max(leftCols.length, rightCols.length);
  for (let i = 0; i < maxRows; i++) {
    if (i < leftCols.length)  lv(doc, leftCols[i][0],  leftCols[i][1],  lc, y, 46);
    if (i < rightCols.length) lv(doc, rightCols[i][0], rightCols[i][1], rc, y, 40);
    y += 5.5;
  }

  // Kitchen Dimensions
  if (kitchenResults.length > 0) {
    y += 4;
    doc.setFontSize(12); doc.setFont("helvetica", "bold"); doc.setTextColor(...ECO7_GREEN);
    doc.text("Kitchen & Hood Dimensions", pw / 2, y, { align: "center" }); y += 4;

    const dimRows = [];
    for (const r of kitchenResults) {
      dimRows.push([
        { content: r.name, rowSpan: 3, styles: { fontStyle: "bold", valign: "middle" } },
        "Cooking Surface",
        String(r.cookL || ""), String(r.cookW || ""),
        String(r.cookNozzles),
      ]);
      dimRows.push(["Exhaust Duct 1", String(r.duct1L || "\u2014"), String(r.duct1W || "\u2014"), String(r.duct1Nozzles || 0)]);
      dimRows.push(["Exhaust Duct 2", String(r.duct2L || "\u2014"), String(r.duct2W || "\u2014"), String(r.duct2Nozzles || 0)]);
    }
    autoTable(doc, {
      startY: y,
      head: [["Kitchen", "Area", `L (${uL})`, `W (${uL})`, "Nozzles"]],
      body: dimRows,
      margin: { left: m, right: m },
      headStyles:  { fillColor: ECO7_GREEN, textColor: WHITE, fontStyle: "bold", fontSize: 8 },
      bodyStyles:  { fontSize: 8 },
      columnStyles: { 0: { cellWidth: 28 }, 1: { cellWidth: 32 }, 4: { halign: "center" } },
    });
  }

  addFooter(doc, pg);

  // ═══ PAGE 2: Per-Kitchen — same style as Aggregated BOM ═══
  if (kitchenResults.length > 0) {
    doc.addPage(); pg++;
    pageHeader(doc, "Eco7 Kitchen Suppression BOM");
    doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(...BRAND_NAVY);
    doc.text("Per-Kitchen Breakdown", m, 54);
    y = 60;

    for (const kitchen of kitchenResults) {
      if (y > 220) { addFooter(doc, pg); doc.addPage(); pg++; y = 20; }

      doc.setFontSize(9); doc.setFont("helvetica", "bold"); doc.setTextColor(...BRAND_NAVY);
      const hdr = `${kitchen.name}  |  Eco7: ${kitchen.agentLiters} L  |  ${kitchen.cylLabel}`;
      doc.text(hdr, m, y);
      const extras = [];
      if (kitchen.cookNozzles  > 0) extras.push(`Cook nozzles: ${kitchen.cookNozzles}`);
      if ((kitchen.duct1Nozzles + kitchen.duct2Nozzles) > 0) extras.push(`Duct nozzles: ${kitchen.duct1Nozzles + kitchen.duct2Nozzles}`);
      if (kitchen.tubingMeters > 0) extras.push(`Tubing: ${kitchen.tubingMeters}m`);
      if (extras.length > 0) {
        doc.setFont("helvetica", "normal"); doc.setTextColor(...GRAY);
        doc.text(extras.join("  |  "), pw - m, y, { align: "right" });
      }
      y += 3;

      const bd = kitchen.items.map(item => [item.pn, item.desc, String(item.qty)]);
      autoTable(doc, {
        startY: y,
        head: [["Part Number", "Description", "Qty"]],
        body: bd,
        margin: { left: m, right: m },
        headStyles: { fillColor: ECO7_GREEN, textColor: WHITE, fontStyle: "bold", fontSize: 9 },
        bodyStyles: { fontSize: 9, textColor: [30, 30, 30] },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        columnStyles: { 0: { cellWidth: 50, fontStyle: "bold" }, 2: { cellWidth: 18, halign: "center", fontStyle: "bold" } },
      });
      y = (doc.lastAutoTable ? doc.lastAutoTable.finalY : y + 20) + 8;
    }
    addFooter(doc, pg);
  }

  // ═══ PAGE 3: Aggregated BOM ═══
  doc.addPage(); pg++;
  pageHeader(doc, "Eco7 Kitchen Suppression BOM");
  y = 54;
  doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(...BRAND_NAVY);
  doc.text("Aggregated Project BOM \u2014 Order Summary", m, y); y += 4;

  doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(...GRAY);
  doc.text(
    `Total Eco7: ${result.totalAgentLiters} L  |  Total Nozzles: ${result.totalNozzles}  |  Kitchens: ${kitchenResults.length}`,
    m, y + 2
  ); y += 10;

  const bomData = aggBom.map(row => [row.pn, row.desc, String(row.qty)]);
  autoTable(doc, {
    startY: y,
    head: [["Part Number", "Description", "Qty"]],
    body: bomData,
    margin: { left: m, right: m },
    headStyles:           { fillColor: ECO7_GREEN, textColor: WHITE, fontStyle: "bold", fontSize: 9 },
    bodyStyles:           { fontSize: 9, textColor: [30, 30, 30] },
    alternateRowStyles:   { fillColor: [240, 240, 240] },
    columnStyles: { 0: { cellWidth: 50, fontStyle: "bold" }, 2: { cellWidth: 18, halign: "center", fontStyle: "bold" } },
  });
  addFooter(doc, pg);

  return doc;
}
